import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { ActionCard } from "../../../../components/ActionCard";
import { useTRPC } from "../../../../trpc";
import { z } from "zod";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { useCallback, useEffect, useMemo } from "react";
import { FullPageLoader } from "../../../../components/FullPageLoader";
import { emailSchema } from "../../../../utils/schema";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { captureEvent } from "../../../../utils/posthog";
import { Event } from "../../../../constants/events";

export const InvoiceSettings = () => {
  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data: account } = useQuery(trpc.getCurrentAccount.queryOptions());

  const { mutate: updateCurrentAccount, isPending } = useMutation(trpc.updateCurrentAccount.mutationOptions());

  const [
    opened,
    {
      open,
      close,
    },
  ] = useDisclosure(false);

  const validate = useMemo(() => zod4Resolver(
    z.object({
      billingEmail: emailSchema(intl),
    }),
  ), [intl]);

  const { onSubmit, getInputProps, setInitialValues, reset } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      billingEmail: account?.billingEmail ?? "",
    },
    validate,
  });

  useEffect(() => {
    if (account) {
      setInitialValues({
        billingEmail: account.billingEmail,
      });
    }
  }, [account]);

  const handleClose = useCallback(() => {
    reset();
    close();
  }, [reset, close]);

  const handleChangeBillingEmail = useCallback((form: { billingEmail: string }) => {
    captureEvent(Event.ChangedAccountBillingEmail);
    updateCurrentAccount(form, {
      onSuccess: (updatedAccount) => {
        queryClient.setQueryData(trpc.getCurrentAccount.queryKey(), updatedAccount);
        handleClose();
      },
    });
  }, [updateCurrentAccount, queryClient, trpc, handleClose]);

  if (!account) {
    return <FullPageLoader />;
  }

  return (
    <>
      <Stack gap="lg">
        <ActionCard
          title={intl.formatMessage({
            defaultMessage: "Billing email preference",
            id: "title.billingEmail",
          })}
          subTitle={account.billingEmail}
        >
          <Button variant="primary" onClick={open}>
            <FormattedMessage defaultMessage="Change email address" id="button.changeEmail" />
          </Button>
        </ActionCard>
      </Stack>

      <Modal
        size="lg"
        opened={opened}
        onClose={handleClose}
        title={
          intl.formatMessage({
            defaultMessage: "Change your billing email address",
            id: "title.changeBillingEmail",
          })
        }
      >
        <Text size="sm" pb="sm">
          <FormattedMessage
            defaultMessage="Current billing email address: {billingEmail}"
            id="text.currentBillingEmail"
            values={{ billingEmail: account.billingEmail }}
          />
        </Text>
        <form onSubmit={onSubmit(handleChangeBillingEmail)}>
          <Stack gap="sm">
            <TextInput
              data-autofocus
              disabled={isPending}
              label={intl.formatMessage({
                defaultMessage: "New billing email address",
                id: "label.newBillingEmail",
              })}
              {...getInputProps("billingEmail")}
            />
          </Stack>
          <Group gap="xs" justify="flex-end" pt="lg">
            <Button type="reset" loading={isPending} onClick={handleClose} variant="default">
              <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
            </Button>

            <Button type="submit" loading={isPending} variant="primary">
              <FormattedMessage defaultMessage="Save" id="button.save" />
            </Button>
          </Group>
        </form>
      </Modal>
    </>
  );
};
